import { ref } from 'vue';
import { useAuthStore } from '@/store/auth';
import type { UlaUser } from '@/types/ula';
import { useUla } from '@/composables/useUla';

import { getConfig } from '@/config';
import { agwClient } from '@tfpl/api-client';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import { permissions } from '@/store/modules/PositionModule';
import { AUTH_RECOVERY_ATTEMPTED_KEY } from '@/constants';

export const useAuthentication = () => {
  const authStore = useAuthStore();
  const { logout: ulaLogout } = useUla();
  const { hasLoggedUserPermission } = useAuthorization();

  const loading = ref(false);
  const loginError = ref(false);
  const initialized = ref(false);
  const isLogged = ref<boolean>(false);

  /**
   * Sets the logged-in user information.
   *
   * @param {Object} payload - The user profile data and token.
   * @param {string} payload.displayName - The user's display name.
   * @param {string} payload.email - The user's email address.
   * @param {string} payload.photoURL - The URL of the user's avatar.
   * @param {string} payload.uuid - The user's UUID.
   * @param {Array} payload.companyPermissions - The user's company permissions.
   */
  const setLoggedUser = async (payload: UlaUser): Promise<void> => {
    let firstName = '';
    let lastName = '';
    if (payload?.displayName) {
      const nameSplit = payload.displayName.split(' ');
      firstName = nameSplit[0];
      lastName = nameSplit[1];
    }

    const mappedPermissions = mapPermissions(payload.companyPermissions);

    authStore.setAgwUser({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: payload.email,
      avatarUrl: payload.photoURL,
      uuid: payload.uuid,
      permissions: payload.companyPermissions,
      mappedPermissions,
    });

    try {
      const segment = payload.token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/');
      const tokenPayload = JSON.parse(atob(segment));
      authStore.setImpersonated(tokenPayload.isImpersonated === true);
      authStore.setImpersonatorUuid(tokenPayload.impersonatorUuid ?? null);
    } catch (e) {
      console.warn('[useAuthentication] Failed to decode JWT payload', e);
      authStore.setImpersonated(false);
      authStore.setImpersonatorUuid(null);
    }

    if (!hasLoggedUserPermission(permissions.HUB_APP.LOGIN)) {
      await logout(false);
      return;
    }

    isLogged.value = true;
    initialized.value = true;
  };

  const initLoggedUser = (loggedUser: Partial<UlaUser>) => {
    void refreshUser(loggedUser);
    console.log('>>> ULA LOGIN SUCCESS:', loggedUser);
    initialized.value = true;
  };

  const refreshUser = async (payload: UlaUser) => {
    setLoggedUser(payload).catch((e) => {
      console.error('[useAuthentication] setLoggedUser failed', e);
    });
    try {
      const user = await authStore.fetchUser();
      authStore.setUser(user);
      // Auth boot succeeded → this tab runs good code; clear the stale-bundle
      // recovery guard so a later mid-session deploy can recover again.
      globalThis.sessionStorage?.removeItem(AUTH_RECOVERY_ATTEMPTED_KEY);
    } catch (e) {
      // fetchUser failed even after the api-client force-refreshed the token and
      // retried (see configureBrowserClients). Recovery (logout+redirect) is driven
      // by onUnauthorized; here we only make sure we never leave the app hanging.
      console.error('[useAuthentication] refreshUser failed', e);
    } finally {
      // Always release the loading gate — the router's beforeEach awaits
      // waitForAuth(), so a stuck isLoading=true white-screens the whole app.
      authStore.setLoading(false);
    }
  };

  const logout = async (notifyUla: boolean) => {
    if (notifyUla) {
      console.log('>>> Notifying ULA about logout...');
      await ulaLogout();
    }
    authStore.setUser(null);
    authStore.setAgwUser(null);
    authStore.setImpersonated(false);
    authStore.setImpersonatorUuid(null);
    globalThis.location.href = `${
      getConfig().ULA_URL
    }?organization=${getConfig().TENANT.toUpperCase()}&application=HUB&logout=true`;
  };

  return {
    initLoggedUser,
    logout,
    refreshUser,
    initialized,
    loading,
    loginError,
  };
};

/*
 * Function for mapping company permissions.
 */
const mapPermissions = (companyPermissions: agwClient.CompanyPermissionsDto[]) => {
  const permissionMaps = new Map();

  // one map per company
  for (const permissionSet of companyPermissions) {
    const permissionMap = new Map();

    for (const subject of permissionSet.permissions) {
      const actions = subject.actions.map((action) => action.name);
      permissionMap.set(subject.name, actions);
    }

    permissionMaps.set(permissionSet.code, permissionMap);
  }

  return permissionMaps;
};
